/**
 * Curated lexicon of stems whose Pāṇinian analysis cannot be recovered from
 * their spelling or from the dictionary's own markup.
 *
 * These are mostly root-nouns (a verbal root used directly as a nominal stem,
 * √bhū → *bhūḥ, bhuvau, bhuvaḥ*) and compounds ending in one (*su-dhī*). A plain
 * stem of the same shape would decline quite differently — *bhvau* rather than
 * *bhuvau* — because 6.4.77 `aci śnudhātubhruvāṃ yvor iyaṅuvaṅau` fires only for
 * verbal roots.
 *
 * Detecting these automatically does not work: matching a stem's tail against
 * the Dhātupāṭha finds *mātṛ-gaṇa* and *nāga-karṇa* for every genuine *kāṣṭhī-bhū*,
 * roughly one hit in twenty-five. So they are curated instead, and each entry is
 * verified against a reference paradigm in the test suite.
 */

import { StemAnalysis } from "./vidyut-engine";
import { CaseName, Gender, Num } from "./types";

export type LexiconEntry = {
    analysis: StemAnalysis;
    /** Short gloss shown in the UI, e.g. "su + √dhī (root-noun)". */
    note?: string;
    /**
     * Forms for cells the engine derives wrongly, replacing whatever it produced.
     *
     * Used only where the engine is demonstrably wrong and the correct form is
     * listed in the grammars — that is, for *nipātana*, forms Pāṇini states
     * outright rather than deriving. Every override records why it is here; none
     * is a convenience.
     */
    overrides?: Partial<Record<CaseName, Partial<Record<Num, string[]>>>>;
    /**
     * A second reading to derive and merge in.
     *
     * Some words are licensed to inflect two ways at once. A masculine bahuvrīhi
     * ending in a feminine stem — *ati-camū*, *bahu-śreyasī* — takes masculine
     * endings, but 1.4.5 `vāmi` and 1.4.6 `ṅiti hrasvaśca` optionally give it
     * nadī endings before `ām` and the ṅit affixes. Both sets are correct, so
     * both are derived and the cells unioned, rather than one being chosen or
     * the other written out by hand.
     */
    alsoAs?: Array<{ gender?: Gender; analysis?: StemAnalysis }>;
};

/**
 * Keyed by stem in IAST. A stem may decline differently per gender, so entries
 * are keyed by gender with `any` as the fallback.
 */
export type LexiconRecord = Partial<Record<Gender, LexiconEntry>> & { any?: LexiconEntry };

/**
 * Roots are given in their *aupadeśika* form — the shape Pāṇini states them in,
 * including the accent marks and `it` letters that condition later rules. `DI\N`
 * is √dhī of the divādi gaṇa; the trailing `N` marks it as ṅit, which is why it
 * takes ātmanepada endings and matters for the derivation.
 *
 * Every entry here is verified against a reference paradigm in
 * `declension.test.ts`; nothing is added on intuition.
 */
export const EXCEPTION_LEXICON: Record<string, LexiconRecord> = {
    // Root-nouns: the stem *is* a verbal root, so 6.4.77 substitutes iyaṅ/uvaṅ
    // before vowel endings — bhuvau, not bhvau.
    "bhū": {
        fem: {
            analysis: { kind: "krdanta", root: "BU", gana: "Bhvadi", krt: "kvip" },
            note: "√bhū (root-noun)",
        },
    },
    "dhī": {
        fem: {
            analysis: { kind: "krdanta", root: "DI\\N", gana: "Divadi", krt: "kvip" },
            note: "√dhī (root-noun)",
        },
    },

    
    // Agent nouns in -tṛ: the -tṛ is the kṛt affix tṛc, not part of the root, and
    // the strong cases lengthen to -tār- (kartāram) which a plain stem never does.
    "kartṛ": {
        mas: {
            analysis: { kind: "krdanta", root: "qukf\\Y", gana: "Tanadi", krt: "tfc" },
            note: "√kṛ + tṛc (agent noun)",
        },
        neu: {
            analysis: { kind: "krdanta", root: "qukf\\Y", gana: "Tanadi", krt: "tfc" },
            note: "√kṛ + tṛc (agent noun)",
            // A neuter agent noun takes either the an-insertion (kartṛṇā) or
            // the masculine weak forms (kartrā); both are attested, Whitney
            // §375. The engine emits only the first of each pair.
            overrides: {
                ins: { s: ["kartṛṇā", "kartrā"] },
                dat: { s: ["kartṛṇe", "kartre"] },
                abl: { s: ["kartṛṇaḥ", "kartuḥ"] },
                gen: { s: ["kartṛṇaḥ", "kartuḥ"], d: ["kartṛṇoḥ", "kartroḥ"] },
                loc: { s: ["kartṛṇi", "kartari"], d: ["kartṛṇoḥ", "kartroḥ"] },
            },
        },
    },

    // -is / -us stems. Their final s becomes ṣ before vowel endings (haviṣā, not
    // havisā) by 8.3.57 `iṇkoḥ`, but 8.3.59 `ādeśapratyayayoḥ` restricts that to
    // an s which is itself an affix or a substitute. Given an opaque stem the
    // engine cannot know that, so the unādi affix has to be stated. The roots
    // below were found by searching the Dhātupāṭha for the derivation that
    // reproduces each stem.
    "havis": {
        neu: {
            analysis: { kind: "krdanta", root: "hu\\", gana: "Juhotyadi", unadi: "isi" },
            note: "√hu + isi",
        },
    },
    "sarpis": {
        neu: {
            analysis: { kind: "krdanta", root: "sf\\px~", gana: "Bhvadi", unadi: "isi" },
            note: "√sṛp + isi",
        },
    },
    "arcis": {
        neu: {
            analysis: { kind: "krdanta", root: "arca~", gana: "Bhvadi", unadi: "isi" },
            note: "√arc + isi",
        },
    },
    "cakṣus": {
        neu: {
            analysis: { kind: "krdanta", root: "ca\\kzi~\\N", gana: "Adadi", unadi: "usi" },
            note: "√cakṣ + usi",
        },
    },
    "dhanus": {
        neu: {
            analysis: { kind: "krdanta", root: "Dana~", gana: "Juhotyadi", unadi: "usi" },
            note: "√dhan + usi",
        },
    },
    "āyus": {
        neu: {
            analysis: { kind: "krdanta", root: "i\\R", gana: "Adadi", unadi: "usi" },
            note: "√i + usi",
        },
    },
    "vapus": {
        neu: {
            analysis: { kind: "krdanta", root: "quva\\pa~^", gana: "Bhvadi", unadi: "usi" },
            note: "√vap + usi",
        },
    },

    // Root-nouns whose final aspirate throws its aspiration back onto the initial
    // consonant when the ending strips it (8.2.37 `eco baśo bhaṣ`): budh gives
    // *bhut*, *bhudbhyām*, *bhutsu*; guh gives *ghuṭ*, *ghuḍbhyām*. The engine
    // applies this only when it knows the stem is a dhātu.
    "budh": {
        mas: {
            analysis: { kind: "krdanta", root: "buDa~", gana: "Bhvadi", krt: "kvip" },
            note: "√budh (root-noun)",
        },
        neu: {
            analysis: { kind: "krdanta", root: "buDa~", gana: "Bhvadi", krt: "kvip" },
            note: "√budh (root-noun)",
        },
    },
    "guh": {
        fem: {
            analysis: { kind: "krdanta", root: "guhU~^", gana: "Bhvadi", krt: "kvip" },
            note: "√guh (root-noun)",
        },
    },
    "pur": {
        fem: {
            analysis: { kind: "krdanta", root: "pura~", gana: "Tudadi", krt: "kvip" },
            note: "√pur (root-noun)",
        },
    },

    // --- compounds -------------------------------------------------------
    // A compound declines through its final member (see StemAnalysis), so what
    // is recorded here is how that member should be described. Each was found by
    // searching the analysis space for one that reproduces the whole reference
    // paradigm; see __search-compounds.test.ts.

    "viśvapā": {
        mas: { analysis: { kind: "compound", prefix: "viśva", final: { kind: "krdanta", root: "pA\\", gana: "Bhvadi", krt: "kvip" } }, note: "viśva + √pā" },
        fem: { analysis: { kind: "compound", prefix: "viśva", final: { kind: "krdanta", root: "pA\\", gana: "Bhvadi", krt: "kvip" } }, note: "viśva + √pā" },
    },
    "anupadeṣṭṛ": {
        mas: { analysis: { kind: "compound", prefix: "anupa", final: { kind: "krdanta", root: "di\\Sa~^", gana: "Tudadi", krt: "tfc" } }, note: "anu-upa + √diś + tṛc" },
    },
    "samrāj": {
        mas: { analysis: { kind: "compound", prefix: "sam", final: { kind: "krdanta", root: "rAjf~^", gana: "Bhvadi", krt: "kvip" } }, note: "sam + √rāj" },
    },
    "upaniṣad": {
        fem: { analysis: { kind: "compound", prefix: "upa", final: { kind: "basic", stem: "niṣad" } }, note: "upa + ni-ṣad" },
    },
    "samidh": {
        mas: { analysis: { kind: "compound", prefix: "sam", final: { kind: "basic", stem: "idh" } }, note: "sam + idh" },
        neu: { analysis: { kind: "compound", prefix: "sam", final: { kind: "basic", stem: "idh" } }, note: "sam + idh" },
    },
    "sumanas": {
        mas: { analysis: { kind: "compound", prefix: "su", final: { kind: "basic", stem: "manas" } }, note: "su + manas" },
        neu: { analysis: { kind: "compound", prefix: "su", final: { kind: "basic", stem: "manas" } }, note: "su + manas" },
    },
    "sujyotis": {
        mas: { analysis: { kind: "compound", prefix: "su", final: { kind: "basic", stem: "jyotis" } }, note: "su + jyotis" },
        neu: { analysis: { kind: "compound", prefix: "su", final: { kind: "basic", stem: "jyotis" } }, note: "su + jyotis" },
    },
    "sucakṣus": {
        mas: { analysis: { kind: "compound", prefix: "su", final: { kind: "basic", stem: "cakṣus" } }, note: "su + cakṣus" },
        neu: { analysis: { kind: "compound", prefix: "su", final: { kind: "basic", stem: "cakṣus" } }, note: "su + cakṣus" },
    },

    // Compounds whose final member is settled but where the engine still omits
    // some optional forms; see the note on 1.4.5/1.4.6 in `optional-nadi.ts`.
    "sudhī": {
        mas: { analysis: { kind: "compound", prefix: "su", final: { kind: "krdanta", root: "DI\\N", gana: "Divadi", krt: "kvip" } }, note: "su + √dhī", alsoAs: [{ gender: "fem" }] },
    },
    "subhrū": {
        mas: { analysis: { kind: "compound", prefix: "su", final: { kind: "basic", stem: "bhrū" } }, note: "su + bhrū", alsoAs: [{ gender: "fem" }] },
    },
    "supis": {
        mas: { analysis: { kind: "compound", prefix: "su", final: { kind: "krdanta", root: "pisf~", gana: "Bhvadi", krt: "kvip" } }, note: "su + √pis", alsoAs: [{ analysis: { kind: "basic", stem: "supis" } }] },
        neu: { analysis: { kind: "compound", prefix: "su", final: { kind: "krdanta", root: "pisf~", gana: "Bhvadi", krt: "kvip" } }, note: "su + √pis", alsoAs: [{ analysis: { kind: "basic", stem: "supis" } }] },
    },
    "sutus": {
        mas: { analysis: { kind: "compound", prefix: "su", final: { kind: "krdanta", root: "tusa~", gana: "Bhvadi", krt: "kvip" } }, note: "su + √tus", alsoAs: [{ analysis: { kind: "basic", stem: "sutus" } }] },
        neu: { analysis: { kind: "compound", prefix: "su", final: { kind: "krdanta", root: "tusa~", gana: "Bhvadi", krt: "kvip" } }, note: "su + √tus", alsoAs: [{ analysis: { kind: "basic", stem: "sutus" } }] },
    },
    "aticamū": {
        mas: { analysis: { kind: "compound", prefix: "ati", final: { kind: "nyap", stem: "camū" } }, note: "ati + camū", alsoAs: [{ gender: "fem" }, { analysis: { kind: "basic", stem: "aticamū" } }] },
    },
    "bahuśreyasī": {
        mas: { analysis: { kind: "compound", prefix: "bahu", final: { kind: "nyap", stem: "śreyasī" } }, note: "bahu + śreyasī", alsoAs: [{ gender: "fem" }] },
    },
    "susakhi": {
        // A mixed paradigm: the strong cases keep sakhi's irregular -āy-
        // (susakhā, susakhāyau) by 7.1.92, while the weak singular is a regular
        // i-stem (susakhinā, susakhaye) because 1.4.7 excludes only the simple
        // word sakhi from ghi. Deriving it through the final member gets the
        // strong cases and the dual right; the five weak-singular cells are
        // left as the engine gives them rather than hand-written.
        mas: {
            analysis: { kind: "compound", prefix: "su", final: { kind: "basic", stem: "sakhi" } },
            note: "su + sakhi",
            alsoAs: [{ analysis: { kind: "basic", stem: "susakhi" } }],
        },
    },
    "sugaṇ": {
        // 8.3.28 `ṅaṇoḥ kuk ṭuk śari` inserts ṭ between a final ṇ and a
        // following sibilant; the engine does not apply it here.
        mas: {
            analysis: { kind: "compound", prefix: "su", final: { kind: "basic", stem: "gaṇ" } },
            note: "su + gaṇ",
            overrides: { loc: { p: ["sugaṇsu", "sugaṇṭsu"] } },
        },
        neu: {
            analysis: { kind: "compound", prefix: "su", final: { kind: "basic", stem: "gaṇ" } },
            note: "su + gaṇ",
            overrides: { loc: { p: ["sugaṇsu", "sugaṇṭsu"] } },
        },
    },

    // √gṝ as a root-noun. The i lengthens before r at the end of a pada
    // (8.2.76 `ra-vor upadhāyā dīrgha ikaḥ`): gīḥ, gīrbhyām, gīrṣu — which an
    // opaque stem does not produce.
    "gir": {
        fem: { analysis: { kind: "krdanta", root: "gF", gana: "Tudadi", krt: "kvip" }, note: "√gṝ (root-noun)" },
    },

    // Feminines in -ī and -ū that are NOT ṅīp/āp derivatives. The shape
    // heuristic would call them nyāp and then 6.1.68 would drop their
    // nominative -s, giving *lakṣmī* for lakṣmīḥ. lakṣmī is a nipātana and
    // vadhū's ū is ūṅ, which 6.1.68 does not cover.
    "lakṣmī": {
        fem: { analysis: { kind: "basic", stem: "lakṣmī" }, note: "nipātana" },
    },
    "vadhū": {
        fem: { analysis: { kind: "basic", stem: "vadhū" }, note: "ūṅ stem" },
    },

    // --- nipātana: 7.1.75 `asthi-dadhi-sakthy-akṣṇām anaṅ udāttaḥ` ---------
    //
    // These four words replace their final vowel with `an` before a vowel-initial
    // ending, and keep it before a consonant-initial one. The engine has the two
    // groups the wrong way round: it gives *asthabhyām* where the stem should
    // stay asthi-, and *asthīnām* where the an-stem is wanted. Everything else
    // it derives correctly (asthnā, asthne, asthnoḥ, asthani/asthni), so only
    // the affected cells are overridden. Forms per Whitney §343.
    "asthi": {
        neu: {
            analysis: { kind: "basic", stem: "asthi" },
            note: "nipātana (7.1.75)",
            overrides: {
                voc: { s: ["asthi", "asthe"] },
                ins: { d: ["asthibhyām"], p: ["asthibhiḥ"] },
                dat: { d: ["asthibhyām"], p: ["asthibhyaḥ"] },
                abl: { d: ["asthibhyām"], p: ["asthibhyaḥ"] },
                gen: { p: ["asthnām"] },
                loc: { p: ["asthiṣu"] },
            },
        },
    },
    "akṣi": {
        neu: {
            analysis: { kind: "basic", stem: "akṣi" },
            note: "nipātana (7.1.75)",
            overrides: {
                voc: { s: ["akṣi", "akṣe"] },
                ins: { d: ["akṣibhyām"], p: ["akṣibhiḥ"] },
                dat: { d: ["akṣibhyām"], p: ["akṣibhyaḥ"] },
                abl: { d: ["akṣibhyām"], p: ["akṣibhyaḥ"] },
                gen: { p: ["akṣṇām"] },
                loc: { p: ["akṣiṣu"] },
            },
        },
    },
};
